// PWA Install Prompt Component for ShreemAstro
// Handles "Add to Home Screen" functionality with user-friendly UI
// Only shows on MOBILE devices (not desktop)

import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Alert, IconButton } from '@mui/joy';
import CloseIcon from '@mui/icons-material/Close';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import IosShareIcon from '@mui/icons-material/IosShare';
import { useIsMobile } from '../hooks/useIsMobile';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  
  // Only show on mobile/tablet devices (not desktop)
  const { isMobile, isTablet } = useIsMobile();
  const isMobileDevice = isMobile || isTablet;

  useEffect(() => {
    // Don't show on desktop at all
    if (!isMobileDevice) {
      console.log('[PWA] Desktop detected - PWA prompt disabled');
      return;
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      console.log('[PWA] Already running in standalone mode');
      setIsInstalled(true);
      return;
    }

    // Detect iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(isIOSDevice);
    console.log('[PWA] Mobile device detected:', isIOSDevice ? 'iOS' : 'Android');

    // Check if user previously dismissed the prompt
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedTime = localStorage.getItem('pwa-install-dismissed-time');
    
    console.log('[PWA] Dismissal check:', { dismissed, dismissedTime });
    
    // Reset dismissal after 7 days
    if (dismissed && dismissedTime) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed > 7) {
        localStorage.removeItem('pwa-install-dismissed');
        localStorage.removeItem('pwa-install-dismissed-time');
        console.log('[PWA] Dismissal reset after 7 days');
      } else {
        console.log('[PWA] Dismissed', daysSinceDismissed.toFixed(1), 'days ago');
      }
    }

    // For iOS devices, always show instructions after delay (iOS doesn't fire beforeinstallprompt)
    if (isIOSDevice) {
      setTimeout(() => {
        console.log('[PWA] Auto-showing iOS install instructions');
        setShowPrompt(true);
      }, 3000); // Reduced to 3 seconds
    }

    // Listen for beforeinstallprompt event (Android mobile only)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      console.log('[PWA] ✓ beforeinstallprompt event fired - PWA installable on mobile device!');
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Auto-show prompt after 3 seconds for Android mobile
      setTimeout(() => {
        console.log('[PWA] Auto-showing Android mobile install prompt');
        setShowPrompt(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      console.log('[PWA] ✓ App installed successfully');
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
      localStorage.removeItem('pwa-install-dismissed');
      localStorage.removeItem('pwa-install-dismissed-time');
    });

    // Debug: Check service worker status
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then(reg => {
        console.log('[PWA] Service Worker status:', reg ? 'Registered' : 'Not registered');
      });
    } else {
      console.log('[PWA] ⚠️ Service Worker not supported');
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isMobileDevice]); // Added dependency

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    await deferredPrompt.prompt();

    // Wait for user response
    const choiceResult = await deferredPrompt.userChoice;
    
    if (choiceResult.outcome === 'accepted') {
      console.log('[PWA] User accepted install');
    } else {
      console.log('[PWA] User dismissed install');
      localStorage.setItem('pwa-install-dismissed', 'true');
      localStorage.setItem('pwa-install-dismissed-time', Date.now().toString());
    }

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
    localStorage.setItem('pwa-install-dismissed-time', Date.now().toString());
    console.log('[PWA] User dismissed install prompt');
  };

  // Don't show if already installed
  if (isInstalled) {
    return null;
  }

  // iOS Install Instructions
  if (isIOS && showPrompt) {
    return (
      <Alert
        variant="soft"
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          right: 16,
          zIndex: 10000,
          maxWidth: 400,
          mx: 'auto',
          boxShadow: 'lg'
        }}
        endDecorator={
          <IconButton size="sm" variant="plain" color="neutral" onClick={handleDismiss}>
            <CloseIcon />
          </IconButton>
        }
      >
        <Box>
          <Typography level="title-sm" startDecorator={<InstallMobileIcon />}>
            Install ShreemAstro App
          </Typography>
          <Typography level="body-sm" sx={{ mt: 1 }}>
            Tap <IosShareIcon sx={{ fontSize: '1rem', verticalAlign: 'middle' }} /> (Share) button
            below and select "Add to Home Screen"
          </Typography>
        </Box>
      </Alert>
    );
  }

  // Android/Desktop Install Prompt
  if (deferredPrompt && showPrompt) {
    return (
      <Alert
        variant="soft"
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          right: 16,
          zIndex: 10000,
          maxWidth: 400,
          mx: 'auto',
          boxShadow: 'lg'
        }}
        endDecorator={
          <IconButton size="sm" variant="plain" color="neutral" onClick={handleDismiss}>
            <CloseIcon />
          </IconButton>
        }
      >
        <Box>
          <Typography level="title-sm" startDecorator={<InstallMobileIcon />}>
            Install ShreemAstro App
          </Typography>
          <Typography level="body-sm" sx={{ mt: 1, mb: 2 }}>
            Get instant access from your home screen - works offline!
          </Typography>
          <Button
            size="sm"
            onClick={handleInstallClick}
            startDecorator={<InstallMobileIcon />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              }
            }}
          >
            Install Now
          </Button>
        </Box>
      </Alert>
    );
  }

  return null;
}

// Manual Install Button Component (for settings/menu)
// Only shows on mobile devices
export function InstallButton() {
  const [canInstall, setCanInstall] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  
  // Only show on mobile/tablet devices
  const { isMobile, isTablet } = useIsMobile();
  const isMobileDevice = isMobile || isTablet;

  useEffect(() => {
    // Don't show install button on desktop
    if (!isMobileDevice) {
      return;
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setCanInstall(false);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isMobileDevice]); // Added dependency

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const choiceResult = await deferredPrompt.userChoice;
    
    if (choiceResult.outcome === 'accepted') {
      console.log('[PWA] User accepted install');
    }

    setDeferredPrompt(null);
    setCanInstall(false);
  };

  if (isInstalled) {
    return (
      <Button
        disabled
        size="sm"
        startDecorator={<InstallMobileIcon />}
        variant="outlined"
      >
        App Installed ✓
      </Button>
    );
  }

  if (!canInstall) {
    return null;
  }

  return (
    <Button
      size="sm"
      onClick={handleInstallClick}
      startDecorator={<InstallMobileIcon />}
      sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        '&:hover': {
          background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
        }
      }}
    >
      Install App
    </Button>
  );
}
